// Admin panel — shows aggregated contextual-teaser performance.
// Lets the team see which per-page teaser messages drive the most chats.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { TrendingUp, Eye, X, MessageSquare } from "lucide-react";

const RANGE_OPTIONS = [7, 30, 90];

export default function AdminTeaserStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/teasers/stats", { params: { days } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Fehler beim Laden"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div data-testid="admin-teaser-stats">
      {/* Range selector */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-xs font-bold tracking-widest uppercase text-neutral-500">Zeitraum:</span>
        {RANGE_OPTIONS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs font-bold tracking-wide border ${
              days === d
                ? "bg-[#FF5A00] text-white border-[#FF5A00]"
                : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
            }`}
            data-testid={`teaser-range-${d}`}
          >
            {d} Tage
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-sm text-neutral-500 py-8" data-testid="teaser-stats-loading">Laden…</div>
      )}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 px-4 py-3 mb-6" data-testid="teaser-stats-error">{error}</div>
      )}

      {data && !loading && (
        <>
          {/* Top summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <SummaryCard icon={Eye} label="Gesamt-Impressionen" value={data.total_shown} />
            <SummaryCard icon={MessageSquare} label="Chats geöffnet" value={data.total_converted} accent />
            <SummaryCard icon={TrendingUp} label="Conversion-Rate" value={`${data.overall_conversion_rate_pct}%`} />
          </div>

          {/* Per-teaser table */}
          <div className="border border-neutral-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="teaser-stats-table">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <Th>Teaser-Key</Th>
                  <Th className="text-end">Angezeigt</Th>
                  <Th className="text-end">Geschlossen</Th>
                  <Th className="text-end">Chat geöffnet</Th>
                  <Th className="text-end">Conversion</Th>
                </tr>
              </thead>
              <tbody>
                {data.by_teaser.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-neutral-500" data-testid="teaser-stats-empty">
                      Noch keine Daten in diesem Zeitraum.
                    </td>
                  </tr>
                ) : (
                  data.by_teaser.map((row) => (
                    <tr key={row.teaser_key} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`teaser-row-${row.teaser_key}`}>
                      <td className="px-4 py-3 font-mono text-[12px] font-bold text-neutral-900">{row.teaser_key}</td>
                      <td className="px-4 py-3 text-end tabular-nums">{row.shown}</td>
                      <td className="px-4 py-3 text-end tabular-nums text-neutral-500">
                        <X size={12} className="inline -mt-0.5 me-0.5 text-neutral-400" />
                        {row.dismissed}
                      </td>
                      <td className="px-4 py-3 text-end tabular-nums font-bold text-emerald-700">{row.converted}</td>
                      <td className="px-4 py-3 text-end">
                        <span className={`inline-block px-2 py-0.5 text-[11px] font-bold ${
                          row.conversion_rate_pct >= 10
                            ? "bg-emerald-100 text-emerald-800"
                            : row.conversion_rate_pct >= 3
                            ? "bg-amber-100 text-amber-800"
                            : "bg-neutral-100 text-neutral-700"
                        }`}>
                          {row.conversion_rate_pct}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-neutral-400 mt-3 leading-relaxed">
            Conversion = Anteil der angezeigten Teaser, bei denen der Besucher den Chat geöffnet hat.
            Höhere Werte deuten auf eine starke Page-Message-Passung hin.
          </p>
        </>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2 text-[11px] font-bold tracking-widest uppercase text-neutral-500 text-start ${className}`}>{children}</th>
);

const SummaryCard = ({ icon: Icon, label, value, accent = false }) => (
  <div className={`p-4 border ${accent ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200 bg-white"}`}>
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={14} className={accent ? "text-[#FF5A00]" : "text-neutral-500"} />
      <span className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">{label}</span>
    </div>
    <div className={`text-3xl font-black tabular-nums ${accent ? "text-[#FF5A00]" : "text-neutral-900"}`}>{value}</div>
  </div>
);
